import { Add, AddCircle, Delete, Group, Person } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import CustomTabPanel from '../core/CustomTabPanel';
import { ACTIVITY_LOG_AREAS } from './activityLogAreas';
import { LegalTransactionSidebar } from './LegalTransactionSidebar';
import { LoadingDialog } from '../core/LoadingDialog';
import { NoDataDialog } from '../core/NoDataDialog';
import { DocumentOverview } from '../Document/DocumentOverview';
import { FSDirectorySearch } from '../FSDirectory/FSDirectorySearch';
import {
  useGetDownloadedDocuments,
  useSendDocuments,
} from '../hooks/reactQuerys/DocumentQueries';
import { useGetHasDirectory } from '../hooks/reactQuerys/FsDirectoryQueries';
import { useGetLegalRequirementStates } from '../hooks/reactQuerys/LegalRequirementQueries';
import {
  useCreateLegalTransactionFullObject,
  useDeleteLegalTransaction,
  useGetLegalTransactionFullObject,
  useGetLegalTransactionProgresses,
  useGetLegalTransactionStates,
  useReactiveLegalTransactionFullObject,
  useUpdateLegalTransactionFullObject,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import { useGetMeetingEmailTemplates } from '../hooks/reactQuerys/MeetingEmailTemplateQueries';
import { badRequestText } from '../hooks/useFetch';
import { RatingFormTab } from './RatingFormTab';
import { IArnoTopObject } from '../Interfaces/IArnoTopObject';
import { IDocument } from '../Interfaces/IDocument';
import { IDocumentDirectory } from '../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../Interfaces/IDocumentDirectoryHasDocument';
import { IDraftMessageFullObject } from '../Interfaces/IDraftMessage';
import { IInvoiceFullObject } from '../Interfaces/IInvoice';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalPhaseFullObject } from '../Interfaces/ILegalPhase';
import { ILegalRole } from '../Interfaces/ILegalRole';
import { ILegalRole_canInvite } from '../Interfaces/ILegalRole_canInvite';
import {
  ILegalTransaction,
  ILegalTransactionDataCollection,
  ILegalTransactionFullObject,
} from '../Interfaces/ILegalTransaction';
import { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import type { ILegalTransactionCustomId } from '../Interfaces/ILegalTransactionCustomId';
import { ILegalTransactionGwGFullObject } from '../Interfaces/ILegalTransactionGwG';
import { type IMeasure } from '../Interfaces/ILegalTransactionMeasure';
import { ILegalTransactionMeeting } from '../Interfaces/ILegalTransactionMeeting';
import { ILegalTransactionMeetingEmail } from '../Interfaces/ILegalTransactionMeetingEmail';
import { ILegaltransactionNote } from '../Interfaces/ILegaltransactionNote';
import { ILegalTransactionRecordNumber } from '../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionRelationship } from '../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionTransactionNumber } from '../Interfaces/ILegalTransactionTransactionNumber';
import type { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import {
  initSendDocumentNew,
  ISendDocumentNew,
  ISendDocumentNewSendOption,
  SendOption,
} from '../Interfaces/ISendDocumentNew';
import type { IUser } from '../Interfaces/IUser';
import type { IUserGroup } from '../Interfaces/IUserGroup';
import { selectableUserGroups } from '../Interfaces/IUserGroup';
import type { State } from '../redux/mainReducer';
import { AddCustomIdDialog } from './AddCustomIdDialog';
import { DocumentDirectoryOverview } from './DocumentDirectory/DocumentDirectoryOverview';
import { GwGSuggestionAlert } from './DocumentDirectory/GwG/GwGSuggestionAlert';
import { IGwGFolder, IGwGFolderSaveObject } from '../Interfaces/IGwGFolder';
import { DocumentTransferProvider } from './DocumentTransfer/DocumentTransferContext';
import { InvoiceTab } from './InvoiceTab/InvoiceTab';
import { LegalPhaseMain } from './LegalPhase/LegalPhaseMain';
import { LegalTransactionClientsOverview } from './LegalTransactionClientsOverview';
import { LegalTransactionDraftOverview } from './LegalTransactionDraftOverview';
import { SpecialFunctionOverview } from './LegalTransactionFunctions/SpecialFunctionOverview';
import { LegalTransactionMeetingOverview } from './LegalTransactionMeeting/LegalTransactionMeetingOverview';
import {
  LegalTransactionMeetingRequestPanel,
  type IRequestMeetingClientConfig,
} from './MeetingRequest/LegalTransactionMeetingRequestPanel';
import { useGetBookingOptions } from '../hooks/reactQuerys/MeetingRequestQueries';
import { NoteTab } from './Notes/NoteTab';
import { SendDocumentNew } from './SendDocuments/SendDocumentNew';
import { SidepanelView } from './SidepanelView';
import { DraggableSidepanel } from './DraggableSidepanel';
import { IConfig } from '../Interfaces/IConfig';
import { LegalTransactionArnoTop } from './LegalTransactionArnoTop';
import { ProcessManagerTab } from './ProcessManager/ProcessManagerTab';
import { CommunicationTab } from '../Communication/CommunicationTab';
import { TabletActionPanel } from './TabletActionPanel';
import { useGetAppArray } from '../hooks/reactQuerys/AppQueries';
import { useGetLegalClientDocumentTypes } from '../hooks/reactQuerys/LegalClientQueries';
import { useGetHealthCheckStatus } from '../hooks/reactQuerys/HealthCheckAccessQueries';
import { useGetTabletSubmissionsForLegalTransaction } from '../hooks/reactQuerys/TabletSubmissionQueries';
import { useReplyToIncoming } from '../hooks/reactQuerys/CommunicationQueries';
import { IPendingReply } from '../Interfaces/ICommunication';
import { IProcessManager } from '../Interfaces/IProcessManager';
import { ITaskFullObject } from '../Interfaces/ITask';
import { TasksTab } from './Tasks/TasksTab';
import { LegalTransactionDataCollection } from './LegalTransactionRelationships/LegalTransactionDataCollection';
import { ILegalTransactionTalkRoomDraft } from '../Interfaces/ILegalTransactionTalkRoom';
import { LegalTransactionExport } from './LegalTransactionExport';
import { LegalTransactionCopy } from './LegalTransactionCopy';
import {
  createEmptyLegalDependencyData,
  ILegalDependencyData,
} from '../Interfaces/ILegalDependency';
import { LegalDependencyProvider } from './LegalDependencyContext';
import { ActivityLog } from '../generic/ActivityLog';
import { HealthCheckDialog } from './HealthCheck/HealthCheckDialog';
import { useHealthCheck, withRuleTitles } from './HealthCheck/useHealthCheck';
import { IHealthCheckContext } from './HealthCheck/types';
import { healthCheckSavePayload } from './HealthCheck/healthCheckSavePayload';
import { IHealthCheckResult } from './HealthCheck/runHealthCheck';

interface IProps {
  config: IConfig;
  idLegalTransaction: number;
  handleClose: (isEdited: boolean) => void;
  users: IUser[];
  userGroups: IUserGroup[];
  legalTransactionTypes: ILegalTransactionType[];
  arnoTopObjects?: IArnoTopObject[];
}

export const SidepanelDocumentContext = createContext<{
  selectedSidepanelDocument: IDocument | undefined;
  setSelectedSidepanelDocument: (document: IDocument) => void;
}>({
  selectedSidepanelDocument: undefined,
  setSelectedSidepanelDocument: () => {},
});

export const useSidepanelDocumentContext = () => {
  return useContext(SidepanelDocumentContext);
};

const convertToBoolean = (value: unknown) => {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else if (typeof value === 'number') {
    return Boolean(value);
  } else {
    return !!value;
  }
};

const getEmptyLegalTransaction = () => {
  return {
    idLegalTransaction: -1,
    Created_at: '2020-02-02',
    Closed_at: null,
    idContact: -1,
    idLegalTransactionState: 1,
    idLegalTransactionType: -1,
    idLegalTransactionProgress: 10,
    idUserGroup: null,
    UserGroup: '',
    Title: '',
    Notary_idUser: -1,
    Remarks: null,
    Employee_idUser: null,
    LastPhaseUpdate_at: null,
    NotarizationDate: null,
    LegalClientArray: [],
    StartEditDate: null,
    UserInformed_LackOfActivity: false,
    LastActivity_at: null,
    // ARNOtop Sync
    idArnoTopObject: null,
    syncWithArnoTop: false,
  } as ILegalTransaction;
};

/** Bereichsfilter im Änderungsprotokoll — spiegelt die Reiter des Vorgangs,
 *  damit der Nutzer die Kategorien nicht neu lernen muss (#1330). */

export const LegalTransactionEdit = ({
  config,
  idLegalTransaction,
  handleClose,
  users,
  userGroups,
  legalTransactionTypes,
  arnoTopObjects,
}: IProps) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();
  // Buchbare Optionen für den Hart-Block: eine Terminaufforderung mit unbuchbarer
  // Vorgabe (Terminart/Standort ohne buchbare Zeiten) darf nicht gespeichert werden.
  const { bookingOptions } = useGetBookingOptions();

  const {
    hasDirectory: hasDirCertificate,
    isLoadingHasDirectory: isLoadingHasDirCertificate,
  } = useGetHasDirectory(1);
  const {
    hasDirectory: hasDirInvoice,
    isLoadingHasDirectory: isLoadingHasDirInvoice,
  } = useGetHasDirectory(2);
  const {
    hasDirectory: hasDirDraft,
    isLoadingHasDirectory: isLoadingHasDirDraft,
  } = useGetHasDirectory(4);

  const {
    legalTransactionStates,
    isLoadingLegalTransactionStates,
    refetchLegalTransactionStates,
  } = useGetLegalTransactionStates();

  const {
    legalTransactionProgresses,
    isLoadingLegalTransactionProgresses,
    refetchLegalTransactionProgresses,
  } = useGetLegalTransactionProgresses();

  const {
    legalRequirementStates,
    isLoadingLegalRequirementStates,
    refetchLegalRequirementStates,
  } = useGetLegalRequirementStates();

  const {
    meetingEmailTemplates,
    isLoadingMeetingEmailTemplates,
    refetchMeetingEmailTemplates,
  } = useGetMeetingEmailTemplates();

  const {
    createLegalTransactionFullObject,
    isPendingCreateLegalTransactionFullObject,
  } = useCreateLegalTransactionFullObject();

  const {
    updateLegalTransactionFullObject,
    isPendingUpdateLegalTransactionFullObject,
  } = useUpdateLegalTransactionFullObject();

  const {
    reactiveLegalTransactionFullObject,
    isPendingReactiveLegalTransactionFullObject,
  } = useReactiveLegalTransactionFullObject();

  const {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  } = useGetLegalTransactionFullObject(idLegalTransaction);

  const {
    downloadedDocuments,
    isLoadingDownloadedDocuments,
    refetchDownloadedDocuments,
  } = useGetDownloadedDocuments();

  const { deleteLegalTransaction, isPendingDeleteLegalTransaction } =
    useDeleteLegalTransaction();

  const { sendDocuments } = useSendDocuments();

  const mimeTypes = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const combinedOptions = [
    ...users
      .filter(user => !user.isNotary && !user.isBlocked)
      .map(user => ({
        type: 'Mitarbeiter',
        id: user.idUser,
        name: `${user.FirstName} ${user.LastName}`,
      })),
    ...selectableUserGroups(userGroups).map(group => ({
      type: 'Gruppe',
      id: group.idUserGroup,
      name: group.UserGroup,
    })),
  ];

  const [isClosed, setIsClosed] = useState(false);
  const [measures, setMeasures] = useState<IMeasure[]>([]);
  const [dataCollectionArray, setDataCollectionArray] = useState<
    ILegalTransactionDataCollection[]
  >([]);

  const [relationships, setRelationships] = useState<
    ILegalTransactionRelationship[]
  >([]);

  const [hasInitialized, setHasInitialized] = useState(false);

  const [recordNumberArray, setRecordNumberArray] = useState<
    ILegalTransactionRecordNumber[]
  >([]);
  const [transactionNumberArray, setTransactionNumberArray] = useState<
    ILegalTransactionTransactionNumber[]
  >([]);
  const [legalTransactionObject, setLegalTransactionObject] = useState(
    getEmptyLegalTransaction()
  );
  const [otherDocumentArray, setOtherDocumentArray] = useState<IDocument[]>([]);
  const [legalDependencyData, setLegalDependencyData] =
    useState<ILegalDependencyData>(createEmptyLegalDependencyData());
  const [certificationArray, setCertificationArray] = useState<IDocument[]>([]);
  const [invoiceArray, setInvoiceArray] = useState<IDocument[]>([]);
  const [invoiceObjectArray, setInvoiceObjectArray] = useState<
    IInvoiceFullObject[]
  >([]);
  const [draftArray, setDraftArray] = useState<IDocument[]>([]);
  const [draftMessageArray, setDraftMessageArray] = useState<
    IDraftMessageFullObject[]
  >([]);
  const [noteArray, setNoteArray] = useState<ILegaltransactionNote[]>([]);
  const [documentArray, setDocumentArray] = useState<
    IDocumentDirectoryHasDocument[]
  >([]);
  const [documentDirectoryArray, setDocumentDirectoryArray] = useState<
    IDocumentDirectory[]
  >([]);
  const [meetingArray, setMeetingArray] = useState<ILegalTransactionMeeting[]>(
    []
  );
  const [meetingEmailArray, setMeetingEmailArray] = useState<
    ILegalTransactionMeetingEmail[]
  >([]);

  const [legalPhaseFullObjectArray, setLegalPhaseFullObjectArray] = useState<
    ILegalPhaseFullObject[]
  >([]);
  const [legalRoles, setLegalRoles] = useState<ILegalRole[]>([]);
  const [legalRoleCanInviteMap, setLegalRoleCanInviteMap] = useState<
    ILegalRole_canInvite[]
  >([]);
  const [gwgArray, setGwgArray] = useState<ILegalTransactionGwGFullObject[]>(
    []
  );
  const [currentTab, setCurrentTab] = useState(6);

  /**
   * GwG-Ordner: die berechnete Sicht kommt vom Backend und wird nur gelesen.
   * Zurueck gehen ausschliesslich die Aenderungen — Konfiguration, Erinnerungen
   * und die im Ordner hochgeladenen bzw. entfernten Dokumentversionen.
   */
  const [gwgFolderChangeArray, setGwgFolderChangeArray] = useState<
    IGwGFolderSaveObject[]
  >([]);
  /** Gesetzt, sobald die GwG-Empfehlung fuer diesen Vorgang abgelehnt wurde. */
  const [gwgSuggestionDismissedAt, setGwgSuggestionDismissedAt] = useState<
    string | null
  >(null);
  /**
   * Zaehler statt Boolean: jeder Klick auf „GwG-Ordner anlegen" erhoeht ihn, die
   * Datenablage reagiert auf die Aenderung. So bleibt der Ausloeser ein reiner
   * Datenfluss von oben nach unten, ohne Ref oder Callback-Hin-und-Zurueck.
   */
  const [gwgCreateSignal, setGwgCreateSignal] = useState(0);

  /** Dokumentarten fuer den GwG-Konfigurationsdialog (`isGwGRelevant`). */
  const { legalClientDocumentTypes } = useGetLegalClientDocumentTypes();

  /**
   * Health Check (#1283). Die Regelkonfiguration kommt aus den Einstellungen,
   * die Abweichungen dieses Vorgangs liegen als lokaler Zustand im Hook und
   * reisen beim Speichern mit.
   *
   * Nur fuer freigeschaltete Benutzer (/admin/dev/healthcheck). Ohne
   * Freischaltung: kein Check beim Speichern, kein Dialog, kein Knopf, und die
   * Abweichungen gehen beim Speichern nicht mit — sonst koennte ein leeres
   * Array die eines freigeschalteten Kollegen wegraeumen.
   */
  const { isHealthCheckEnabled } = useGetHealthCheckStatus();
  const {
    ruleStates: healthCheckRuleStates,
    result: healthCheckResult,
    runCheck: runHealthCheckNow,
    clearResult: clearHealthCheckResult,
    overrideArray: healthCheckOverrideArray,
    toggleRule: toggleHealthCheckRule,
    resetRule: resetHealthCheckRule,
    resetOverrideArray: resetHealthCheckOverrideArray,
  } = useHealthCheck({
    idLegalTransactionType: legalTransactionObject.idLegalTransactionType,
    legalTransactionTypeName: legalTransactionObject.LegalTransactionType,
    initialOverrideArray: [],
    enabled: isHealthCheckEnabled,
  });

  const [isHealthCheckDialogOpen, setIsHealthCheckDialogOpen] = useState(false);
  /**
   * Gesetzt, wenn der Health Check aus dem Speichervorgang kommt. Nur dann gibt
   * es „Dennoch speichern"; ein manuell ausgeloester Check hat die Option nicht.
   */
  const [pendingHealthCheckSave, setPendingHealthCheckSave] = useState<{
    migrateType?: boolean;
    openUnterlagenSenden?: boolean;
  } | null>(null);

  /**
   * Nachschlagedaten fuer die Regeln, die nicht im Vorgangs-Objekt stehen.
   * Als Funktion und nicht als Memo, weil der Health Check punktuell laeuft und
   * dabei immer den aktuellen Dialogzustand sehen soll.
   */
  const healthCheckContext = (): IHealthCheckContext => ({
    legalRoles,
    savedIdLegalTransactionType:
      legalTransactionFullObject?.idLegalTransactionType,
    legalTransactionStateName: (legalTransactionStates || []).find(
      state =>
        state.idLegalTransactionState ===
        legalTransactionObject.idLegalTransactionState
    )?.LegalTransactionState,
  });

  /**
   * Aenderung an einem GwG-Ordner uebernehmen. Je Ordner wird genau ein
   * Aenderungsobjekt gefuehrt — das jeweils letzte gewinnt, weil die Datenablage
   * den vollstaendigen Zustand des Ordners mitschickt.
   */
  const handleGwGFolderChange = (
    idDocumentDirectory: number,
    _folder: IGwGFolder,
    saveObject: IGwGFolderSaveObject
  ) => {
    setGwgFolderChangeArray(previous => [
      ...previous.filter(
        entry => entry.idDocumentDirectory !== idDocumentDirectory
      ),
      saveObject,
    ]);
  };

  // Tablet-Dienst-Erweiterung: nur einblenden, wenn die App-Erweiterung
  // ``dino_tablet_service`` lokal aktiv ist. Badge zeigt offene Tablet-
  // Eingaben (Status `submitted`), damit der Notar sie nicht uebersieht.
  const { appArray: _appArrayForTablet } = useGetAppArray();
  /** Terminabhaengige GwG-Erinnerungen setzen das Kalender-Modul voraus. */
  const calendarModuleActive =
    _appArrayForTablet?.find(a => a.system_key === 'calendar')?.IsEffective ===
    true;
  const tabletServiceActive =
    _appArrayForTablet?.find(a => a.system_key === 'dino_tablet_service')
      ?.IsEffective === true;
  const hbciConnectionActive =
    _appArrayForTablet?.find(a => a.system_key === 'hbci_connection')
      ?.IsEffective === true;
  const { submissions: _tabletSubmissionsForBadge } =
    useGetTabletSubmissionsForLegalTransaction(
      tabletServiceActive && idLegalTransaction > 0 ? idLegalTransaction : null
    );
  const openTabletInputCount = (_tabletSubmissionsForBadge ?? []).filter(
    s => s.Status === 'submitted'
  ).length;

  const [
    showSendDocumentsDialogConfirmation,
    setShowSendDocumentsDialogConfirmation,
  ] = useState(false);

  const [assignedLegalClients, setAssignedLegalClients] = useState<
    ILegalClient[]
  >([]);
  // Pro-Mandant-Konfiguration der Terminaufforderung (wer + Terminart/Standort/Notiz).
  const [requestMeetingClientConfig, setRequestMeetingClientConfig] = useState<
    IRequestMeetingClientConfig[]
  >([]);
  // Ident-Service: angehakte Beteiligte der Portal-Prüfung. Die Auswahl gehört
  // zum Vorgang und geht mit dem Speichern raus — Haken setzen fordert auf,
  // Haken entfernen beendet die Aufforderung.
  const [portalCheckSelection, setPortalCheckSelection] = useState<Set<number>>(
    new Set()
  );
  const [customIds, setCustomIds] = useState<ILegalTransactionCustomId[]>([]);
  const [isOpenChangeType, setIsOpenChangeType] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [
    legalTransaction_has_LegalTransactionSpecialFunctionArray,
    setLegalTransaction_has_LegalTransactionSpecialFunctionArray,
  ] = useState<ILegalTransaction_has_LegalTransactionSpecialFunction[]>([]);
  const [processManagerArray, setProcessManagerArray] = useState<
    IProcessManager[]
  >([]);
  const [tasks, setTasks] = useState<ITaskFullObject[]>([]);
  const [selectedType, setSelectedType] = useState('user');

  const [showAddCustomIdDialog, setShowAddCustomIdDialog] = useState(false);

  const [savedLegalTransaction, setSavedLegalTransaction] = useState<
    ILegalTransactionFullObject | undefined
  >(undefined);
  const [isSavePossible, setIsSavePossible] = useState<
    (boolean | Record<string, boolean>)[]
  >([]);

  const isInitialized = isSavePossible.length > 0;

  const [pendingCommunicationReply, setPendingCommunicationReply] =
    useState<IPendingReply | null>(null);
  const { replyToIncoming } = useReplyToIncoming();

  //Nextcloud
  const [talkRooms, setTalkRooms] = useState<ILegalTransactionTalkRoomDraft[]>(
    []
  );

  ////////////////////////// NEU BAUMERT UNTERLAGEN SENDEN

  const [showSendDocumentsDialogNew, setShowSendDocumentsDialogNew] =
    useState(false);

  const [showVorgangWillBeSavedDialog, setShowVorgangWillBeSavedDialog] =
    useState(false);

  const [sendDocumentNew, setSendDocumentNew] = useState<ISendDocumentNew>(() =>
    initSendDocumentNew(idLegalTransaction)
  );

  useEffect(() => {
    if (dataCollectionArray.length > 0 && !isInitialized) {
      const newSaveState = dataCollectionArray.map(item => {
        if ('idMortgage' in item) {
          return {
            accordionOwners: true,
            accordionRealEstate: true,
            accordionMortgageDetails: true,
            accordionBeneficiary: true,
          };
        } else {
          return true;
        }
      });

      setIsSavePossible(newSaveState);
    }
  }, [dataCollectionArray]);

  useEffect(() => {
    isButtonDisabled();
  }, [isSavePossible]);

  const isButtonDisabled = () => {
    // Iteriere durch das Array
    for (const item of isSavePossible) {
      if (typeof item === 'boolean') {
        // Wenn es ein boolean ist, überprüfen, ob es true ist
        if (!item) {
          return true; // Ein false bedeutet, dass der Button deaktiviert werden soll
        }
      } else if (typeof item === 'object' && item !== null) {
        // Wenn es ein Record ist, überprüfe, ob alle Werte true sind
        const allTrue = Object.values(item).every(value => value === true);
        if (!allTrue) {
          return true; // Ein false in einem der Werte bedeutet, dass der Button deaktiviert werden soll
        }
      }
    }

    // SyncPrüfung
    if (
      Boolean(legalTransactionObject?.syncWithArnoTop) === true &&
      legalTransactionObject?.idArnoTopObject == undefined
    ) {
      return true;
    }
    if (
      dataCollectionArray.find(dc => dc.syncWithArnoTop) &&
      legalTransactionObject?.idArnoTopObject == undefined
    ) {
      return true;
    }
    if (
      relationships.find(dc => dc.syncWithArnoTop) &&
      legalTransactionObject?.idArnoTopObject == undefined
    ) {
      return true;
    }

    // Wenn wir hier sind, bedeutet das, dass alle Werte true sind
    return false; // Wenn alles `true` ist, soll der Button aktiv bleiben
  };

  const localSetter = (legalTransactionToSet: ILegalTransactionFullObject) => {
    legalTransactionToSet.UserInformed_LackOfActivity = convertToBoolean(
      legalTransactionToSet.UserInformed_LackOfActivity
    );

    setIsClosed(
      legalTransactionToSet.Closed_at !== null ||
        legalTransactionToSet.idLegalTransactionState === 100
    );
    setDataCollectionArray(legalTransactionToSet.DataCollection);
    setRelationships(legalTransactionToSet.RelationshipArray ?? []);

    setLegalTransactionObject(legalTransactionToSet);
    setSelectedType(() => {
      return legalTransactionToSet.Employee_idUser !== null ||
        legalTransactionToSet.idUserGroup === null
        ? 'user'
        : 'group';
    });

    setGwgArray(legalTransactionToSet.LegalTransactionGwGArray);
    setAssignedLegalClients(legalTransactionToSet.LegalClientArray);
    // Pro-Mandant-Konfiguration der Terminaufforderung aus dem geladenen Stand
    // aufbauen (ausgewählte Mandanten + ihre Terminart/Standort/Notiz-Vorgaben),
    // damit ein erneutes Öffnen/Speichern die Vorgaben nicht verliert.
    setRequestMeetingClientConfig(
      (legalTransactionToSet.LegalClientArray ?? [])
        .filter(c => Boolean(c.RequestClientMeetingSelected))
        .map(c => ({
          idLegalClient: c.idLegalClient,
          idMeetingType: c.RequestMeetingType_idMeetingType ?? null,
          idLocation: c.RequestMeetingLocation_idLocation ?? null,
          note: c.RequestMeetingNote ?? '',
        }))
    );
    // Ident-Service: gespeicherte Auswahl übernehmen, damit der Haken nach dem
    // Speichern (und nach jedem erneuten Öffnen) stehen bleibt.
    setPortalCheckSelection(
      new Set(
        (legalTransactionToSet.LegalClientArray ?? [])
          .filter(c => Boolean(c.RequestClientDataSelected))
          .map(c => c.idLegalClient)
      )
    );
    setLegalRoles(legalTransactionToSet.LegalRoleArray);
    setLegalRoleCanInviteMap(legalTransactionToSet.LegalRoleCanInviteArray);

    setOtherDocumentArray(legalTransactionToSet.OtherDocumentArray ?? []);
    if (legalTransactionToSet.legalDependencyData) {
      setLegalDependencyData(legalTransactionToSet.legalDependencyData);
    } else {
      setLegalDependencyData(createEmptyLegalDependencyData());
    }
    setCertificationArray(legalTransactionToSet.CertificateArray);
    setInvoiceArray(legalTransactionToSet.InvoiceArray);
    setInvoiceObjectArray(legalTransactionToSet.InvoiceObjectArray);
    setDraftArray(legalTransactionToSet.DraftArray);
    setDraftMessageArray(legalTransactionToSet.DraftMessageArray);

    setNoteArray(legalTransactionToSet.NoteArray);

    // Setzen der Ablage
    setDocumentDirectoryArray(legalTransactionToSet.DocumentDirectoryArray);
    setDocumentArray(legalTransactionToSet.DocumentArray);
    // Termine
    setMeetingArray(legalTransactionToSet.LegalTransactionMeetingArray);
    setMeetingEmailArray(
      legalTransactionToSet.LegalTransactionMeetingEmailArray
    );

    setMeasures(legalTransactionToSet.Measurements || []);
    //Nextcloud
    setTalkRooms(legalTransactionToSet.LegalTransactionTalkRoomArray ?? []);

    if (legalTransactionToSet.customIds) {
      setCustomIds(legalTransactionToSet.customIds);
    }

    //Recordnumber und Transactionnumber
    //prüfe ob Recordnumber und Transactionnumber vorhanden sind, wenn nicht, dann füge ein leeres Element hinzu
    if (legalTransactionToSet.RecordNumberArray.length === 0) {
      setRecordNumberArray([
        {
          idLegalTransactionRecordNumber: -1,
          RecordNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
    } else {
      setRecordNumberArray(legalTransactionToSet.RecordNumberArray);
    }
    if (legalTransactionToSet.TransactionNumberArray.length === 0) {
      setTransactionNumberArray([
        {
          TransactionNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
    } else {
      setTransactionNumberArray(legalTransactionToSet.TransactionNumberArray);
    }
    setLegalTransaction_has_LegalTransactionSpecialFunctionArray(
      legalTransactionToSet.LegalTransaction_has_LegalTransactionSpecialFunctionArray
    );
    setProcessManagerArray(legalTransactionToSet.ProcessManagerArray);
    setTasks(legalTransactionToSet.Tasks);
    // Prüfen nach Log
    if (legalTransactionToSet.LogArray !== undefined) {
    }

    // Health Check (#1283): Abweichungen des Vorgangs uebernehmen und das
    // Ergebnis eines frueheren Durchlaufs verwerfen — der Vorgang oeffnet ohne
    // Warnleisten, erst ein Health Check fuellt sie wieder.
    resetHealthCheckOverrideArray(
      legalTransactionToSet.HealthCheckOverrideArray ?? []
    );
    clearHealthCheckResult();

    setLegalPhaseFullObjectArray([
      ...legalTransactionToSet.LegalPhaseArray.map((currentLegalPhase, idx) =>
        currentLegalPhase.Position !== null
          ? {
              ...currentLegalPhase,
              isComplete: Boolean(currentLegalPhase.isComplete),
            }
          : {
              ...currentLegalPhase,
              Position: idx + 1,
              isComplete: Boolean(currentLegalPhase.isComplete),
            }
      ),
    ]);

    setHasInitialized(true);
  };

  useEffect(() => {
    if (!legalTransactionFullObject) return;
    setHasInitialized(false);
    setSavedLegalTransaction(undefined);
    localSetter(legalTransactionFullObject);
  }, [legalTransactionFullObject]);

  useEffect(() => {
    if (!hasInitialized || savedLegalTransaction) return;
    const currentLegalTransactionObject = createLegalTransactionSaveObject();
    setSavedLegalTransaction(currentLegalTransactionObject);
  }, [hasInitialized, savedLegalTransaction]);

  const checkIfEdited = () =>
    !isEqual(createLegalTransactionSaveObject(), savedLegalTransaction);

  const createLegalTransactionSaveObject = () => {
    const uploadObject: ILegalTransactionFullObject = {
      ...legalTransactionObject,
      UserInformed_LackOfActivity: Boolean(
        legalTransactionObject.UserInformed_LackOfActivity
      ),
      DataCollection: dataCollectionArray,
      RelationshipArray: relationships,
      LegalPhaseArray: legalPhaseFullObjectArray,
      LegalClientArray: assignedLegalClients,
      LegalTransactionGwGArray: gwgArray,
      CertificateArray: certificationArray,
      InvoiceArray: invoiceArray,
      InvoiceObjectArray: invoiceObjectArray,
      OtherDocumentArray: otherDocumentArray,
      legalDependencyData: legalDependencyData,
      DraftArray: draftArray,
      DraftMessageArray: draftMessageArray,
      NoteArray: noteArray,
      LegalRoleArray: legalRoles,
      LegalRoleCanInviteArray: legalRoleCanInviteMap,
      LegalTransactionMeetingArray: meetingArray,
      LegalTransactionMeetingEmailArray: meetingEmailArray,
      DocumentArray: documentArray,
      DocumentDirectoryArray: documentDirectoryArray,
      /**
       * GwG-Ordner: nur die Aenderungen. Die Konfiguration ist je Ordner
       * autoritativ, Uploads sind additiv und Loeschungen explizit — der Ordner
       * sieht nur den GwG-Ausschnitt der Mandanten-Dokumente und darf nichts
       * darueber hinaus anfassen.
       */
      GwGFolderChangeArray: gwgFolderChangeArray,
      GwGSuggestionDismissed_at: gwgSuggestionDismissedAt,
      LegalTransaction_has_LegalTransactionSpecialFunctionArray:
        legalTransaction_has_LegalTransactionSpecialFunctionArray,
      ProcessManagerArray: processManagerArray,
      Tasks: tasks,
      //remove empty recordNumbers
      RecordNumberArray: recordNumberArray.filter(
        recordNumber => recordNumber.RecordNumberEntry !== ''
      ),
      TransactionNumberArray: transactionNumberArray.filter(
        transactionNumber => transactionNumber.TransactionNumberEntry !== ''
      ),
      customIds,
      Measurements: measures,
      syncWithArnoTop: Boolean(legalTransactionObject.syncWithArnoTop),
      // Echtes Boolean senden — MySQL liefert das tinyint als 0/1, der
      // Swagger-Validator akzeptiert beim FullObject-Save aber nur boolean/null.
      RequestClientMeeting: Boolean(
        legalTransactionObject.RequestClientMeeting
      ),
      // RequestClientData wie RequestClientMeeting als echtes Boolean senden:
      // Das Feld steckt über den Spread von legalTransactionObject ohnehin im
      // Payload, und der Swagger-Validator akzeptiert beim FullObject-Save nur
      // boolean/null (das rohe tinyint 0/1 löst sonst einen 400 aus).
      // Vorgangsweites Flag aus der Auswahl ableiten: ohne angehakten
      // Beteiligten gibt es keine Aufforderung mehr.
      RequestClientData: portalCheckSelection.size > 0,
      // Pro-Mandant-Konfiguration der Termin-Aufforderung (nur wenn aktiv).
      // Enthaltene Mandanten = ausgewählt (Mail + Portal-CTA). Backend
      // persistiert Vorgaben + dedupliziert den Mailversand per SentAt.
      RequestMeetingClientConfig: legalTransactionObject.RequestClientMeeting
        ? requestMeetingClientConfig
        : [],
      // DiNo Ident-Service: die Portal-Prüfung wird mit dem Vorgang gespeichert.
      // Die Liste ist autoritativ — ein entfernter Haken beendet die
      // Aufforderung serverseitig und sperrt den Upload im Portal wieder.
      RequestDataMailClientIds: Array.from(portalCheckSelection),
      //NextCloud
      LegalTransactionTalkRoomArray: talkRooms,
      /**
       * Health Check (#1283): bewusst OHNE Regeltitel. Die Titel kommen erst
       * beim Absenden dazu (`withRuleTitles`) — hier wuerden sie den
       * Aenderungsvergleich in `checkIfEdited` dauerhaft anschlagen lassen, weil
       * der geladene Vorgang sie nicht enthaelt.
       */
      HealthCheckOverrideArray: healthCheckOverrideArray,
    };
    return uploadObject;
  };

  const transactionInputRef = useRef<HTMLInputElement>(null);
  const recordInputRef = useRef<HTMLInputElement>(null);
  const [focusTransaction, setFocusTransaction] = useState(false);
  const [focusRecord, setFocusRecord] = useState(false);
  const [selectedSidepanelDocument, setSelectedSidepanelDocument] = useState<
    IDocument | undefined
  >(undefined);

  const isPending =
    isPendingCreateLegalTransactionFullObject ||
    isPendingUpdateLegalTransactionFullObject ||
    isPendingDeleteLegalTransaction ||
    isPendingReactiveLegalTransactionFullObject;

  const customIdsWithContact = useMemo(
    () =>
      customIds.map(customId => ({
        ...customId,
        legalClient: assignedLegalClients.find(
          c => c.idLegalClient === customId.idLegalClient
        )!,
      })),
    [customIds, assignedLegalClients]
  );

  const hasMoreCustomIds = useMemo(() => {
    const customLen = (customIdsWithContact ?? []).length;
    const assignedLen = (assignedLegalClients ?? []).length;
    return customLen < assignedLen;
  }, [customIdsWithContact, assignedLegalClients]);

  /**
   * Beteiligte juristische Personen — über **beide** Wege: als Mandant des
   * Vorgangs und als Beteiligter eines Rechtsgeschäfts. Eine als
   * Vertragsgegenstand erfasste Firma hängt nur am Rechtsgeschäft; wer allein
   * die Mandanten zählt, hält den Vorgang für frei von juristischen Personen —
   * dann bleibt die GwG-Empfehlung aus und der Ordner zeigt keine Zeile.
   *
   * Bewusst hier abgeleitet und nicht aus `suggestGwGFolder` des Backends
   * übernommen: eine gerade hinterlegte Firma soll sofort wirken und nicht erst
   * nach dem Speichern.
   */
  const gwgLegalPersonArray = useMemo(() => {
    const legalPersonById = new Map<number, ILegalClient>();

    const addLegalPerson = (legalClient: ILegalClient | undefined) => {
      if (
        legalClient === undefined ||
        legalClient.idLegalClientType !== 2 ||
        legalPersonById.has(legalClient.idLegalClient)
      ) {
        return;
      }

      legalPersonById.set(legalClient.idLegalClient, legalClient);
    };

    assignedLegalClients.forEach(addLegalPerson);
    relationships.forEach(relationship =>
      (relationship.Members ?? []).forEach(member =>
        addLegalPerson(member.LegalClient)
      )
    );

    return Array.from(legalPersonById.values());
  }, [assignedLegalClients, relationships]);

  /** Namen derselben Beteiligten — macht die Empfehlung konkret. */
  const gwgLegalPersonNameArray = useMemo(
    () =>
      gwgLegalPersonArray.map(
        legalClient => legalClient.LastName?.trim() || 'Unbekannte Organisation'
      ),
    [gwgLegalPersonArray]
  );

  useEffect(() => {
    if (focusTransaction && transactionInputRef.current) {
      transactionInputRef.current.focus();
      setFocusTransaction(false);
    }
  }, [focusTransaction]);

  useEffect(() => {
    if (focusRecord && recordInputRef.current) {
      recordInputRef.current.focus();
      setFocusRecord(false);
    }
  }, [focusRecord]);

  const handleSave = async (
    migrateType?: boolean,
    openUnterlagenSenden?: boolean
  ) => {
    setIsOpenChangeType(false);

    // P1: Jede offene Grundbuch-Nachverfolgung (Typ 10/11) muss mindestens eine
    // Automatisierung haben (z. B. „Maßnahme auf erledigt"). Sonst Speichern
    // blockieren, damit keine Nachverfolgung ohne Wirkung angelegt wird.
    const trackingsOhneAutomation = processManagerArray.filter(
      pm =>
        [10, 11].includes(pm.idProcessManagerType) &&
        pm.idProcessManagerState === 1 &&
        (pm.Automations?.length ?? 0) === 0
    );
    if (trackingsOhneAutomation.length > 0) {
      showAlert({
        severity: 'warning',
        text: `Jede Grundbuch-Nachverfolgung braucht mindestens eine Automatisierung (z. B. „Maßnahme auf erledigt"). ${trackingsOhneAutomation.length} Nachverfolgung(en) ohne Automatisierung — bitte im Vorgangsmanager unter „Informationsverfolgung" ergänzen.`,
      });
      return;
    }
    // Hart-Block: unbuchbare Terminaufforderung (Terminart/Standort ohne buchbare
    // Zeiten) nicht speichern — sonst käme der Mandant in eine Sackgasse.
    if (legalTransactionObject.RequestClientMeeting) {
      if (bookingOptions.length === 0) {
        showAlert({
          severity: 'error',
          text: 'Keine buchbaren Zeiten konfiguriert — die Terminaufforderung kann nicht gespeichert werden. Bitte zuerst buchbare Zeiten anlegen.',
        });
        setCurrentTab(5);
        return;
      }
      for (const c of requestMeetingClientConfig) {
        if (c.idMeetingType == null) continue;
        const opt = bookingOptions.find(
          o => o.idMeetingType === c.idMeetingType
        );
        if (!opt) {
          showAlert({
            severity: 'error',
            text: 'Eine vorgegebene Terminart hat keine buchbaren Zeiten. Bitte die Vorgabe anpassen.',
          });
          setCurrentTab(5);
          return;
        }
        if (
          c.idLocation != null &&
          !opt.locations.some(l => l.idLocation === c.idLocation)
        ) {
          showAlert({
            severity: 'error',
            text: 'Ein vorgegebener Standort ist für die Terminart nicht buchbar. Bitte die Vorgabe anpassen.',
          });
          setCurrentTab(5);
          return;
        }
      }
    }

    /**
     * Health Check (#1283). Er ersetzt die bisher hier verstreuten
     * Warnungen — unter anderem den Bestaetigungsdialog zur
     * Rechnungserinnerung, der jetzt die Regel `client.no-invoice-reminder`
     * ist.
     *
     * Warnungen verhindern das Speichern nie. Gibt es welche, uebernimmt der
     * Dialog: „Zurueck" bricht ab, „Dennoch speichern" ruft
     * `persistLegalTransaction`. Hinweise (`info`) zaehlen nicht und halten den
     * Speichervorgang nicht auf.
     *
     * Ohne Freischaltung laeuft kein Check — es wird direkt gespeichert.
     */
    if (!isHealthCheckEnabled) {
      await persistLegalTransaction(migrateType, openUnterlagenSenden);
      return;
    }

    const healthCheck = runHealthCheckNow({
      legalTransaction: createLegalTransactionSaveObject(),
      context: healthCheckContext(),
    });

    if (healthCheck.warningCount > 0) {
      setPendingHealthCheckSave({ migrateType, openUnterlagenSenden });
      setIsHealthCheckDialogOpen(true);
      return;
    }

    await persistLegalTransaction(
      migrateType,
      openUnterlagenSenden,
      healthCheck
    );
  };

  /**
   * Der eigentliche Speichervorgang, abgetrennt von `handleSave`, damit
   * „Dennoch speichern" im Health-Check-Dialog denselben Weg nimmt.
   *
   * Das Upload-Objekt wird hier neu gebaut: im Dialog koennen Pruefungen fuer
   * diesen Vorgang umgeschaltet worden sein, die dann mitgehen muessen.
   */
  const persistLegalTransaction = async (
    migrateType?: boolean,
    openUnterlagenSenden?: boolean,
    /**
     * Das Health-Check-Ergebnis, ueber das der Nutzer entschieden hat.
     * Bewusst als Parameter und nicht aus dem State gelesen: `runCheck` setzt
     * den State, aber React aktualisiert ihn erst nach diesem Durchlauf — im
     * direkten Speicherweg waere er hier noch der alte.
     */
    healthCheckResultForLog?: IHealthCheckResult
  ) => {
    const uploadObject = createLegalTransactionSaveObject();
    uploadObject.migrateType = migrateType;
    if (isHealthCheckEnabled) {
      // Regeltitel nur fuer den Log-Eintrag (#1330), nicht im Save-Objekt selbst.
      uploadObject.HealthCheckOverrideArray = withRuleTitles(
        healthCheckOverrideArray
      );
      // Reist nur mit, damit „mit N Warnungen gespeichert" ins Protokoll kann.
      uploadObject.HealthCheckResult = healthCheckResultForLog
        ? healthCheckSavePayload(healthCheckResultForLog)
        : undefined;
    } else {
      // Nicht mitgeschickt heisst fuer das Backend: Bestand unangetastet.
      uploadObject.HealthCheckOverrideArray = undefined;
      uploadObject.HealthCheckResult = undefined;
    }

    try {
      if (legalTransactionObject.idLegalTransaction < 0) {
        const newData = await createLegalTransactionFullObject(uploadObject);
        localSetter(newData);
      } else {
        const newData = await updateLegalTransactionFullObject(uploadObject);
        localSetter(newData);
      }

      setSavedLegalTransaction(undefined);

      queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      // Task-Fullobject-Cache leeren: die Aufgaben-Dokumente wurden ggf. beim
      // Vorgang-Speichern geändert (hinzugefügt/gelöscht). removeQueries statt
      // invalidateQueries, damit beim erneuten Öffnen einer Aufgabe frisch
      // geladen wird und kein veralteter Zwischenstand entsteht.
      queryClient.removeQueries({ queryKey: ['task/fullObject'] });

      await refetchLegalTransactionFullObject();

      if (openUnterlagenSenden) {
        setShowSendDocumentsDialogNew(true);
      }

      showAlert({
        severity: 'success',
        text: 'Vorgang erfolgreich gespeichert!',
      });

      if (pendingCommunicationReply) {
        const replySnapshot = pendingCommunicationReply;
        setPendingCommunicationReply(null);
        replyToIncoming(replySnapshot).catch(() => {
          showAlert({
            severity: 'warning',
            text: 'Vorgang gespeichert — die Antwort konnte jedoch nicht gesendet werden.',
          });
        });
      }
    } catch (error) {
      const reason = badRequestText(error);
      showAlert({
        severity: 'error',
        text: reason
          ? `Fehler beim Speichern des Vorgangs: ${reason}`
          : 'Fehler beim Speichern des Vorgangs!',
      });
    }
  };

  const handleReactive = async () => {
    setIsOpenChangeType(false);

    const uploadObject = createLegalTransactionSaveObject();

    try {
      const newData = await reactiveLegalTransactionFullObject(uploadObject);
      localSetter(newData);

      setSavedLegalTransaction(undefined);

      queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      // Task-Fullobject-Cache leeren (siehe Haupt-Speichern).
      queryClient.removeQueries({ queryKey: ['task/fullObject'] });

      await refetchLegalTransactionFullObject();

      showAlert({
        severity: 'success',
        text: 'Vorgang wurde erfolgreich reaktiviert!',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Reaktivieren des Vorgangs!',
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteLegalTransaction(idLegalTransaction);

      queryClient.invalidateQueries({
        queryKey: ['legaltransaction', 'search'],
      });

      navigate(`/legaltransaction`);

      showAlert({
        severity: 'success',
        text: 'Vorgang erfolgreich gelöscht!',
      });
    } catch {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Löschen des Vorgangs!',
      });
    }
  };

  const handleDeleteRecordNumber = (index: number) => {
    if (recordNumberArray.length > 1) {
      const newArray = recordNumberArray.filter((_, i) => i !== index);
      setRecordNumberArray(newArray);
    }
  };

  const handleAddRecordNumber = () => {
    if (
      recordNumberArray[recordNumberArray.length - 1].RecordNumberEntry !== ''
    ) {
      // Negative tempId, damit das Backend beim Speichern den Mapping-Schritt
      // tempId → echte idLegalTransactionRecordNumber durchführen kann und
      // Rechnungen, die im selben Speichervorgang dieser RecordNumber zugeordnet
      // werden, ihre Referenz behalten.
      const minId = recordNumberArray.reduce(
        (acc, r) => Math.min(acc, r.idLegalTransactionRecordNumber ?? 0),
        0
      );
      const tempId = Math.min(-1, minId - 1);
      setRecordNumberArray([
        ...recordNumberArray,
        {
          idLegalTransactionRecordNumber: tempId,
          RecordNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
      setFocusRecord(true);
    }
  };

  const handleChangeRecordNumber = (index: number, value: string) => {
    setRecordNumberArray(
      recordNumberArray.map((recordNumber, i) =>
        i === index
          ? { ...recordNumber, RecordNumberEntry: value }
          : recordNumber
      )
    );
  };

  const handleSendDocuments = async () => {
    const hasRecipients = (sendDocumentNew.legalClientArray?.length ?? 0) > 0;

    const hasSelection =
      (sendDocumentNew.documentTypeArray?.length ?? 0) > 0 ||
      (sendDocumentNew.selectedDocumentIds?.length ?? 0) > 0 ||
      (sendDocumentNew.selectedDocuments?.length ?? 0) > 0;

    const requiresEmailText = sendDocumentNew.sendOption === SendOption.Email;
    const hasEmailText =
      typeof sendDocumentNew.emailText === 'string' &&
      sendDocumentNew.emailText.trim().length > 0;

    if (
      !hasRecipients ||
      !hasSelection ||
      (requiresEmailText && !hasEmailText)
    ) {
      showAlert({
        severity: 'warning',
        text: 'Bitte mindestens einen Mandanten und mindestens ein Dokument/Typ auswählen.',
      });
      return;
    }

    try {
      await sendDocuments(sendDocumentNew);

      showAlert({
        severity: 'success',
        text: 'Dokumente erfolgreich versendet',
      });
    } catch (error) {
      showAlert({
        severity: 'error',
        text: 'Fehler beim Versenden der Dokumente',
      });
    }
  };

  const handleDeleteTransactionNumber = (index: number) => {
    if (transactionNumberArray.length > 1) {
      const newArray = transactionNumberArray.filter((_, i) => i !== index);
      setTransactionNumberArray(newArray);
    }
  };

  const handleChangeTransactionNumber = (index: number, value: string) => {
    setTransactionNumberArray(
      transactionNumberArray.map((transactionNumber, i) =>
        i === index
          ? { ...transactionNumber, TransactionNumberEntry: value }
          : transactionNumber
      )
    );
  };

  const handleChangeCustomIds = (contactId: number, customId: string) => {
    setCustomIds(prevCustomIds => {
      if (prevCustomIds.find(item => item.idLegalClient === contactId)) {
        return prevCustomIds.map(item =>
          item.idLegalClient === contactId
            ? { ...item, customId: customId }
            : item
        );
      } else {
        return [
          ...prevCustomIds,
          {
            idLegalTransaction: idLegalTransaction,
            idLegalClient: contactId,
            customId,
          },
        ];
      }
    });
  };

  const handleDeleteCustomId = (contactId: number) => {
    setCustomIds(prevCustomIds =>
      prevCustomIds.filter(
        customId =>
          customId.idLegalClient !== contactId &&
          customId.idLegalTransaction === idLegalTransaction
      )
    );
  };

  const handleAddTransactionNumber = () => {
    if (
      transactionNumberArray[transactionNumberArray.length - 1]
        .TransactionNumberEntry !== ''
    ) {
      setTransactionNumberArray([
        ...transactionNumberArray,
        {
          TransactionNumberEntry: '',
          idLegalTransaction: idLegalTransaction,
        },
      ]);
      setFocusTransaction(true);
    }
  };

  if (
    isLoadingDownloadedDocuments ||
    isLoadingLegalTransactionStates ||
    isLoadingLegalTransactionProgresses ||
    isLoadingLegalRequirementStates ||
    isLoadingMeetingEmailTemplates ||
    isFetchingLegalTransactionFullObject
  )
    return <LoadingDialog unmount={() => handleClose(false)} />;

  if (
    legalTransactionFullObject === undefined ||
    downloadedDocuments === undefined ||
    legalTransactionStates === undefined ||
    legalTransactionProgresses === undefined ||
    legalRequirementStates === undefined ||
    meetingEmailTemplates === undefined
  )
    return (
      <NoDataDialog
        unmount={() => handleClose(false)}
        refetch={() => {
          refetchLegalTransactionFullObject();
          refetchDownloadedDocuments();
          refetchLegalTransactionStates();
          refetchLegalTransactionProgresses();
          refetchLegalRequirementStates();
          refetchMeetingEmailTemplates();
        }}
      />
    );

  return (
    <SidepanelDocumentContext.Provider
      value={{
        selectedSidepanelDocument,
        setSelectedSidepanelDocument,
      }}
    >
      {/* Stellt allen Dokumentlisten des Vorgangs das Verschieben in einen
          anderen Bereich und das Umbenennen bereit. */}
      <DocumentTransferProvider
        idLegalTransaction={idLegalTransaction}
        certificateArray={certificationArray}
        setCertificateArray={setCertificationArray}
        invoiceArray={invoiceArray}
        setInvoiceArray={setInvoiceArray}
        draftArray={draftArray}
        setDraftArray={setDraftArray}
        otherDocumentArray={otherDocumentArray}
        setOtherDocumentArray={setOtherDocumentArray}
        legalPhaseArray={legalPhaseFullObjectArray}
        setLegalPhaseArray={setLegalPhaseFullObjectArray}
        documentDirectoryArray={documentDirectoryArray}
        setDocumentDirectoryArray={setDocumentDirectoryArray}
        documentArray={documentArray}
        setDocumentArray={setDocumentArray}
        invoiceObjectArray={invoiceObjectArray}
        setInvoiceObjectArray={setInvoiceObjectArray}
        idLegalTransactionType={legalTransactionObject.idLegalTransactionType}
        assignedLegalClients={assignedLegalClients}
        legalRoles={legalRoles}
      >
        <Dialog
          open
          onClose={() => {
            if (isPendingUpdateLegalTransactionFullObject) return;
            handleClose(checkIfEdited());
          }}
          maxWidth='xl'
          fullWidth
          scroll='paper'
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider',
              p: 2,
            }}
          >
            <Typography variant='h5'>
              {idLegalTransaction > 0 && legalTransactionFullObject
                ? legalTransactionFullObject.Title
                : 'Neues Rechtsgeschäft'}
            </Typography>

            {/* Container für die Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant='outlined'
                onClick={() => {
                  setShowVorgangWillBeSavedDialog(true);
                }}
                disabled={isPending || isClosed}
              >
                {isPending ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    <span>Unterlagen senden</span>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  'Unterlagen senden '
                )}
              </Button>

              <LegalTransactionExport
                legalTransaction={legalTransactionFullObject}
                hasUnsavedChanges={checkIfEdited()}
                onSaveBeforeExport={async () => {
                  await handleSave();
                }}
              />

              {idLegalTransaction > 0 && legalTransactionFullObject && (
                <LegalTransactionCopy
                  legalTransaction={legalTransactionFullObject}
                />
              )}

              {/*
            <Button
              variant='outlined'
              onClick={() => {
                handleExportPdf();
              }}
              disabled={isPending}
            >
              {isPendingExportPDF ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Export Nebenakte</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Export Nebenakte'
              )}
            </Button>

            <Button
              variant='outlined'
              onClick={() => {
                handleExportXjustiz();
              }}
              disabled={isPending}
            >
              {isPendingExportLegalTransaction ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Export XJustiz</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Export XJustiz'
              )}
            </Button>

             */}

              {idLegalTransaction > 0 && legalTransactionFullObject && (
                <IconButton
                  disabled={isClosed}
                  onClick={() => setIsOpenDelete(true)}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          </Box>

          <DialogContent>
            {isPending && <LinearProgress sx={{ mt: 1 }} />}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {isClosed && (
                <Alert severity='warning'>
                  Dieser Vorgang ist bereits geschlossen und kann nicht mehr
                  angepasst werden.
                </Alert>
              )}

              {/*
                #1283: Die frueheren festen Warnleisten an dieser Stelle — kein
                Status "Laufend" und geaenderte Rechtsgeschaeftsart — sind jetzt
                Health-Check-Regeln (`master.not-visible-for-clients`,
                `master.type-change-needs-migration`). Sie werden bewusst NUR
                im Health Check angezeigt und nicht zusaetzlich im Vorgang, weil
                die Arbeitsflaeche sonst schnell zulaeuft.
              */}
            </Box>

            <Box>
              {/* #1181: Seitenansicht als schwebendes, verschiebbares Overlay
                statt als schmale Spalte. Das Formular/die Tabelle behält so die
                volle Dialogbreite; das PDF legt sich darüber und kann per
                Griffleiste beiseite gezogen werden. */}
              {selectedSidepanelDocument && (
                <DraggableSidepanel>
                  <SidepanelView
                    document={selectedSidepanelDocument}
                    mimeTypes={mimeTypes}
                    onClose={() => setSelectedSidepanelDocument(undefined)}
                  />
                </DraggableSidepanel>
              )}
              <Box>
                <Grid container spacing={2} mt={1}>
                  <Grid item sm={12}>
                    <TextField
                      label='Bezeichnung'
                      value={legalTransactionObject.Title}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          Title: event.target.value,
                        })
                      }
                      size='small'
                      fullWidth
                      disabled={isClosed}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      label='Art'
                      value={legalTransactionObject.idLegalTransactionType}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          idLegalTransactionType: Number(event.target.value),
                        })
                      }
                      size='small'
                      fullWidth
                      select
                      disabled={isClosed}
                    >
                      {legalTransactionTypes.map(legalTransactionType => (
                        <MenuItem
                          key={`idLegalTransactionType-${legalTransactionType.idLegalTransactionType}`}
                          value={legalTransactionType.idLegalTransactionType}
                        >
                          {legalTransactionType.LegalTransactionType}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      label='Zugewiesener Notar'
                      value={legalTransactionObject.Notary_idUser}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          Notary_idUser: Number(event.target.value),
                        })
                      }
                      size='small'
                      fullWidth
                      select
                      disabled={isClosed}
                    >
                      {users
                        .filter(user => Boolean(user.isNotary))
                        .map(user => (
                          <MenuItem
                            key={`Notary_idUser-${user.idUser}`}
                            value={user.idUser}
                          >
                            {user.FirstName} {user.LastName}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>

                  <Grid item sm={3}>
                    <Autocomplete
                      options={combinedOptions}
                      groupBy={option => option.type}
                      getOptionLabel={option => option.name}
                      disabled={isClosed}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={
                            selectedType === 'user' ? (
                              <span>
                                <Person
                                  style={{
                                    verticalAlign: 'middle',
                                    marginRight: 8,
                                  }}
                                />
                                Zugewiesener Mitarbeiter
                              </span>
                            ) : (
                              <span>
                                <Group
                                  style={{
                                    verticalAlign: 'middle',
                                    marginRight: 8,
                                  }}
                                />
                                Zugewiesene Gruppe
                              </span>
                            )
                          }
                          size='small'
                          variant='outlined'
                        />
                      )}
                      renderOption={(props, option) => (
                        <ListItem {...props} key={option.id}>
                          <ListItemText primary={option.name} />
                        </ListItem>
                      )}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          if (newValue.type === 'Mitarbeiter') {
                            setLegalTransactionObject({
                              ...legalTransactionObject,
                              Employee_idUser: newValue.id,
                              idUserGroup: null,
                            });
                            setSelectedType('user');
                          } else if (newValue.type === 'Gruppe') {
                            setLegalTransactionObject({
                              ...legalTransactionObject,
                              idUserGroup: newValue.id,
                              Employee_idUser: null,
                            });
                            setSelectedType('group');
                          }
                        } else {
                          setLegalTransactionObject({
                            ...legalTransactionObject,
                            Employee_idUser: null,
                            idUserGroup: null,
                          });
                        }
                      }}
                      value={
                        selectedType === 'user' &&
                        legalTransactionObject.Employee_idUser !== null
                          ? combinedOptions.find(
                              option =>
                                option.type === 'Mitarbeiter' &&
                                option.id ===
                                  legalTransactionObject.Employee_idUser
                            )
                          : selectedType === 'group' &&
                              legalTransactionObject.idUserGroup !== null
                            ? combinedOptions.find(
                                option =>
                                  option.type === 'Gruppe' &&
                                  option.id ===
                                    legalTransactionObject.idUserGroup
                              )
                            : null
                      }
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <TextField
                      label='Datum der Beurkundung'
                      placeholder=''
                      type='date'
                      value={legalTransactionObject.NotarizationDate}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          NotarizationDate: event.target.value,
                        })
                      }
                      size='small'
                      disabled={isClosed}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      label='Phase'
                      value={legalTransactionObject.idLegalTransactionProgress}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          idLegalTransactionProgress: Number(
                            event.target.value
                          ),
                        })
                      }
                      size='small'
                      fullWidth
                      select
                      disabled={isClosed}
                    >
                      {legalTransactionProgresses.map(
                        legalTransactionProgress => (
                          <MenuItem
                            key={`idLegalTransactionProgress-${legalTransactionProgress.idLegalTransactionProgress}`}
                            value={
                              legalTransactionProgress.idLegalTransactionProgress
                            }
                          >
                            {legalTransactionProgress.LegalTransactionProgress}
                          </MenuItem>
                        )
                      )}
                    </TextField>
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      label='Status'
                      value={legalTransactionObject.idLegalTransactionState}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          idLegalTransactionState: Number(event.target.value),
                        })
                      }
                      size='small'
                      fullWidth
                      select
                      disabled={isClosed}
                    >
                      {legalTransactionStates.map(legalTransactionState => (
                        <MenuItem
                          key={`idLegalTransactionState-${legalTransactionState.idLegalTransactionState}`}
                          value={legalTransactionState.idLegalTransactionState}
                          disabled={legalTransactionState.isDisabled}
                        >
                          {legalTransactionState.LegalTransactionState}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item sm={3}>
                    {transactionNumberArray.map((transactionNumber, index) => (
                      <Box
                        key={`TransactionNumber-${index}`}
                        display='flex'
                        alignItems='center'
                      >
                        <TextField
                          label={`Vorgangsnummer`}
                          id={`TransactionNumber-${index}`}
                          value={transactionNumber.TransactionNumberEntry}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            handleChangeTransactionNumber(
                              index,
                              event.target.value
                            )
                          }
                          onBlur={(
                            event: React.FocusEvent<HTMLInputElement>
                          ) => {
                            if (event.target.value === '') {
                              handleDeleteTransactionNumber(index);
                            }
                          }}
                          size='small'
                          fullWidth
                          disabled={isClosed}
                          inputRef={
                            index === transactionNumberArray.length - 1
                              ? transactionInputRef
                              : null
                          }
                        />
                        {transactionNumberArray.length > 1 &&
                          index < transactionNumberArray.length - 1 &&
                          transactionNumberArray[index + 1]
                            ?.TransactionNumberEntry !== '' && (
                            <IconButton
                              onClick={() =>
                                handleDeleteTransactionNumber(index)
                              }
                            >
                              <Delete />
                            </IconButton>
                          )}
                        {index === transactionNumberArray.length - 1 &&
                          transactionNumber.TransactionNumberEntry !== '' && (
                            <IconButton onClick={handleAddTransactionNumber}>
                              <AddCircle />
                            </IconButton>
                          )}
                      </Box>
                    ))}
                  </Grid>

                  <Grid item sm={3}>
                    {recordNumberArray.map((recordNumber, index) => (
                      <Box
                        key={`RecordNumber-${index}`}
                        display='flex'
                        alignItems='center'
                      >
                        <TextField
                          label={`Urkundennummer`}
                          id={`RecordNumber-${index}`}
                          value={recordNumber.RecordNumberEntry}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            handleChangeRecordNumber(index, event.target.value)
                          }
                          onBlur={(
                            event: React.FocusEvent<HTMLInputElement>
                          ) => {
                            if (event.target.value === '') {
                              handleDeleteRecordNumber(index);
                            }
                          }}
                          size='small'
                          fullWidth
                          disabled={isClosed}
                          inputRef={
                            index === recordNumberArray.length - 1
                              ? recordInputRef
                              : null
                          }
                        />
                        {recordNumberArray.length > 1 &&
                          index < recordNumberArray.length - 1 &&
                          recordNumberArray[index + 1].RecordNumberEntry !==
                            '' && (
                            <IconButton
                              onClick={() => handleDeleteRecordNumber(index)}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        {index === recordNumberArray.length - 1 &&
                          recordNumber.RecordNumberEntry !== '' && (
                            <IconButton onClick={handleAddRecordNumber}>
                              <AddCircle />
                            </IconButton>
                          )}
                      </Box>
                    ))}
                  </Grid>
                  <Grid item sm={12}>
                    <Box
                      gap={1}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant='h6'>Mandantenkennungen</Typography>
                      <Tooltip
                        title={
                          !hasMoreCustomIds
                            ? 'Es wurde bereits jedem Mandanten eine Kennung zugewiesen.'
                            : undefined
                        }
                      >
                        <Box>
                          <Button
                            variant='text'
                            onClick={() => setShowAddCustomIdDialog(true)}
                            startIcon={<Add />}
                            disabled={!hasMoreCustomIds}
                          >
                            Hinzufügen
                          </Button>
                        </Box>
                      </Tooltip>
                    </Box>
                    <Grid item container sm={12} spacing={2} mt={1}>
                      {customIdsWithContact.map((x, i) => (
                        <Grid item sm={4} key={i}>
                          <TextField
                            key={i + 1}
                            label={`Mandantenkennung: ${x.legalClient?.FirstName} ${x.legalClient?.LastName}`}
                            value={x.customId}
                            size='small'
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              handleChangeCustomIds(
                                x.idLegalClient,
                                event.target.value
                              )
                            }
                            fullWidth
                            InputProps={{
                              endAdornment: (
                                <Tooltip title='Kennung löschen'>
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteCustomId(x.idLegalClient)
                                    }
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              ),
                            }}
                          >
                            {x.customId}
                          </TextField>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>

                <Box sx={{ my: 5 }}>
                  <Stepper
                    activeStep={
                      legalTransactionObject?.idLegalTransactionProgress
                    }
                    alternativeLabel
                  >
                    {legalTransactionProgresses.map(
                      legalTransactionProgress => (
                        <Step
                          key={
                            legalTransactionProgress.idLegalTransactionProgress
                          }
                          completed={
                            Number(
                              legalTransactionObject?.idLegalTransactionProgress
                            ) >
                            legalTransactionProgress.idLegalTransactionProgress
                          }
                          active={
                            Number(
                              legalTransactionObject?.idLegalTransactionProgress
                            ) ===
                            legalTransactionProgress.idLegalTransactionProgress
                          }
                        >
                          <StepLabel>
                            {legalTransactionProgress.LegalTransactionProgress}
                          </StepLabel>
                        </Step>
                      )
                    )}
                  </Stepper>
                </Box>

                {config.App.includes('arno_top') && (
                  <LegalTransactionArnoTop
                    legalTransactionObject={legalTransactionObject}
                    setLegalTransactionObject={setLegalTransactionObject}
                    relationships={relationships}
                    setRelationships={setRelationships}
                    dataCollection={dataCollectionArray}
                    setDataCollection={setDataCollectionArray}
                    takenLegalClients={assignedLegalClients}
                    arnotopObjects={arnoTopObjects}
                    legalTransactionTypes={legalTransactionTypes}
                  />
                )}

                {/*
                  #1283: Die frueheren Rechnungs-Warnleisten — Urkundennummer
                  ohne Rechnung (`InvoiceWarning`) und die Schuldner-Hinweise je
                  Zeile (`DebtorWarningBars`) — sind jetzt Health-Check-Regeln
                  und werden ausschliesslich dort angezeigt.
                */}

                <Grid container>
                  <Grid item md={2} xs={3}>
                    <LegalTransactionSidebar
                      currentTab={currentTab}
                      onChange={setCurrentTab}
                      noteCount={
                        // #1195: nur offene Notizen — zählt beim Erledigen
                        // sofort runter, Kreis verschwindet bei 0.
                        noteArray.filter(
                          x => x.idLegalTransactionNote > 0 && !x.Closed_at
                        ).length
                      }
                      openTaskCount={
                        tasks.filter(x => x.Closed_at === null).length
                      }
                      draftCount={draftArray.length + draftMessageArray.length}
                      certificateCount={certificationArray.length}
                      invoiceCount={
                        invoiceArray.length + invoiceObjectArray.length
                      }
                      participantCount={assignedLegalClients.length}
                      openTabletInputCount={openTabletInputCount}
                      tabletServiceActive={tabletServiceActive}
                      pendingCommunication={!!pendingCommunicationReply}
                    />
                  </Grid>

                  <Grid item md={10} xs={9}>
                    <CustomTabPanel value={currentTab} index={0}>
                      <ProcessManagerTab
                        legalTransaction={legalTransactionFullObject}
                        legalClients={assignedLegalClients}
                        relationsships={relationships}
                        setRelationships={setRelationships}
                        //
                        processManagerArray={processManagerArray}
                        setProcessManagerArray={setProcessManagerArray}
                        //
                        documentArray={documentArray}
                        documentDirectoryArray={documentDirectoryArray}
                        setDocumentArray={setDocumentArray}
                        setDocumentDirectoryArray={setDocumentDirectoryArray}
                        //
                        legalRequirementStates={legalRequirementStates}
                        legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                        setLegalPhaseFullObjectArray={
                          setLegalPhaseFullObjectArray
                        }
                        //
                        legalTransaction_has_LegalTransactionSpecialFunctionArray={
                          legalTransaction_has_LegalTransactionSpecialFunctionArray
                        }
                        setLegalTransaction_has_LegalTransactionSpecialFunctionArray={
                          setLegalTransaction_has_LegalTransactionSpecialFunctionArray
                        }
                        gwgArray={gwgArray}
                        setGwgArray={setGwgArray}
                      />
                      {/*
                    <MeasureTab
                      measures={measures}
                      setMeasures={setMeasures}
                      idLegalTransaction={idLegalTransaction}
                      legalTransactionHasSpecialFunctions={
                        legalTransaction_has_LegalTransactionSpecialFunctionArray
                      }
                      setLegalTransactionHasSpecialFunctions={
                        setLegalTransaction_has_LegalTransactionSpecialFunctionArray
                      }
                      gwgs={gwgArray}
                      setGwgs={setGwgArray}
                      legalPhaseFullObjects={legalPhaseFullObjectArray}
                      legalRequirementStates={legalRequirementStates}
                    />
                    */}
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={1}>
                      <LegalTransactionDataCollection
                        legalTransaction={legalTransactionFullObject}
                        isSavePossible={isSavePossible}
                        relationships={relationships}
                        setRelationships={setRelationships}
                        dataCollection={dataCollectionArray}
                        setDataCollection={setDataCollectionArray}
                        setAssignedLegalClients={setAssignedLegalClients}
                        assignedLegalClients={assignedLegalClients}
                        documentArray={documentArray}
                        documentDirectoryArray={documentDirectoryArray}
                        setDocumentArray={setDocumentArray}
                        setDocumentDirectoryArray={setDocumentDirectoryArray}
                        legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                        setLegalPhaseFullObjectArray={
                          setLegalPhaseFullObjectArray
                        }
                        legalReqStates={legalRequirementStates}
                        setIsSavePossible={setIsSavePossible}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={2}>
                      <NoteTab
                        notesArray={noteArray}
                        setNotesArray={setNoteArray}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={3}>
                      <TasksTab
                        legalTransaction={createLegalTransactionSaveObject()}
                        tasks={tasks}
                        setTasks={setTasks}
                        users={users}
                        userGroups={userGroups}
                        legalClients={assignedLegalClients}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={4}>
                      <LegalTransactionDraftOverview
                        draftArray={draftArray}
                        setDraftArray={setDraftArray}
                        draftMessageArray={draftMessageArray}
                        setDraftMessageArray={setDraftMessageArray}
                        recordNumberArray={recordNumberArray}
                        transactionNumberArray={transactionNumberArray}
                        hasDirDraft={hasDirDraft || false}
                        assignedLegalClients={assignedLegalClients}
                        idLegalTransaction={idLegalTransaction}
                        idLegalTransactionType={
                          legalTransactionObject.idLegalTransactionType
                        }
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={5}>
                      <LegalTransactionMeetingOverview
                        legalClients={assignedLegalClients}
                        idLegalTransaction={idLegalTransaction}
                        idLegalTransactionType={
                          legalTransactionObject.idLegalTransactionType
                        }
                        meetingArray={meetingArray}
                        setMeetingArray={setMeetingArray}
                        meetingEmailArray={meetingEmailArray}
                        setMeetingEmailArray={setMeetingEmailArray}
                        talkRooms={talkRooms}
                        setTalkRooms={setTalkRooms}
                      />
                      <LegalTransactionMeetingRequestPanel
                        idLegalTransaction={idLegalTransaction}
                        requestClientMeeting={
                          legalTransactionObject.RequestClientMeeting ?? false
                        }
                        onRequestClientMeetingChange={value => {
                          setLegalTransactionObject(prev => ({
                            ...prev,
                            RequestClientMeeting: value,
                          }));
                          // Beim Aktivieren NIEMANDEN vorauswählen — der Mitarbeiter
                          // hakt gezielt die Mandanten an, die einen Termin machen sollen.
                          // Beim Deaktivieren die Auswahl leeren.
                          if (!value) {
                            setRequestMeetingClientConfig([]);
                          }
                        }}
                        legalClients={assignedLegalClients}
                        requestMeetingClientConfig={requestMeetingClientConfig}
                        onRequestMeetingClientConfigChange={
                          setRequestMeetingClientConfig
                        }
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={6}>
                      <LegalDependencyProvider
                        value={{ legalDependencyData, setLegalDependencyData }}
                      >
                        <LegalPhaseMain
                          legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                          setLegalPhaseFullObjectArray={
                            setLegalPhaseFullObjectArray
                          }
                          legalReqState={legalRequirementStates}
                          downloadedArray={downloadedDocuments}
                          legalClients={assignedLegalClients}
                          legaltransactionId={idLegalTransaction}
                          startdatedeadline={
                            legalTransactionObject.NotarizationDate &&
                            legalTransactionObject.NotarizationDate
                              ? new Date(
                                  legalTransactionObject.NotarizationDate
                                )
                              : legalTransactionObject.StartEditDate &&
                                  legalTransactionObject.StartEditDate
                                ? new Date(legalTransactionObject.StartEditDate)
                                : undefined
                          }
                          idAdministrativeUnitTransaction={
                            legalTransactionObject.idAdministrativeUnit ?? null
                          }
                          onChangeAdministrativeUnitTransaction={newId =>
                            setLegalTransactionObject({
                              ...legalTransactionObject,
                              idAdministrativeUnit: newId ?? undefined,
                            })
                          }
                        />
                      </LegalDependencyProvider>
                    </CustomTabPanel>
                    <CustomTabPanel value={currentTab} index={7}>
                      <LegalTransactionClientsOverview
                        idLegalTransaction={idLegalTransaction}
                        setCustomIds={setCustomIds}
                        legalRoles={legalRoles}
                        setLegalRoles={setLegalRoles}
                        legalRoleCanInviteMap={legalRoleCanInviteMap}
                        setLegalRoleCanInviteMap={setLegalRoleCanInviteMap}
                        takenLegalClients={assignedLegalClients}
                        setTakenLegalClients={setAssignedLegalClients}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={8}>
                      <Typography variant='h6' sx={{ mb: 2 }}>
                        Kommunikation
                      </Typography>
                      <CommunicationTab
                        idLegalTransaction={
                          legalTransactionObject.idLegalTransaction
                        }
                        onPendingReply={setPendingCommunicationReply}
                        hasPendingReply={pendingCommunicationReply !== null}
                        printContext={{
                          title: legalTransactionObject.Title,
                          dinoNumber: legalTransactionObject.idLegalTransaction,
                          fileNumbers: transactionNumberArray
                            .map(t => t.TransactionNumberEntry)
                            .filter(Boolean),
                          recordNumbers: recordNumberArray
                            .map(r => r.RecordNumberEntry)
                            .filter(Boolean),
                          notaryIdUser: legalTransactionObject.Notary_idUser,
                          employeeIdUser:
                            legalTransactionObject.Employee_idUser,
                          userGroupId: legalTransactionObject.idUserGroup,
                        }}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={9}>
                      <DocumentOverview
                        key='certification'
                        allowRemove={true}
                        allowUpload={true}
                        title='Urkunden'
                        documentArray={certificationArray}
                        setDocumentArray={setCertificationArray}
                        documentLocation={{ kind: 'certificate' }}
                        downloadedArray={downloadedDocuments}
                        legalClients={assignedLegalClients}
                        enableSidepanel={true}
                      />
                      {hasDirCertificate && legalTransactionFullObject && (
                        <Accordion sx={{ mt: 5 }}>
                          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                            Ordnersuche
                          </AccordionSummary>
                          <AccordionDetails>
                            <FSDirectorySearch
                              key='certification-fs-search'
                              searchArray={recordNumberArray
                                .filter(
                                  recordNumber =>
                                    recordNumber.RecordNumberEntry !== ''
                                )
                                .map(
                                  recordNumber => recordNumber.RecordNumberEntry
                                )}
                              searchArrayFallback={transactionNumberArray
                                .filter(
                                  transactionNumber =>
                                    transactionNumber.TransactionNumberEntry !==
                                    ''
                                )
                                .map(
                                  transactionNumber =>
                                    transactionNumber.TransactionNumberEntry
                                )}
                              searchItem='certificate'
                              documentArray={certificationArray}
                              setDocumentArray={setCertificationArray}
                            />
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={10}>
                      <>
                        <InvoiceTab
                          legalTransaction={legalTransactionObject}
                          documentArray={invoiceArray}
                          setDocumentArray={setInvoiceArray}
                          downloadedArray={downloadedDocuments}
                          invoiceObjectArray={invoiceObjectArray}
                          setInvoiceObjectArray={setInvoiceObjectArray}
                          assignedLegalClients={assignedLegalClients}
                          setLegalClients={setAssignedLegalClients}
                          legalRoles={legalRoles}
                          legalTransactionTypes={legalTransactionTypes}
                          hasDirInvoice={hasDirInvoice || false}
                          recordNumberArray={recordNumberArray}
                          transactionNumberArray={transactionNumberArray}
                        />
                      </>
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={11}>
                      <DocumentOverview
                        key='other-documents'
                        allowRemove={true}
                        allowUpload={true}
                        title='Öffentliche Dokumente'
                        documentArray={otherDocumentArray}
                        setDocumentArray={setOtherDocumentArray}
                        documentLocation={{ kind: 'other' }}
                        downloadedArray={downloadedDocuments}
                        legalClients={assignedLegalClients}
                        enableSidepanel={true}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={12}>
                      {/* Proaktive Empfehlung: eine juristische Person ist am
                          Vorgang beteiligt — dann lohnt ein GwG-Ordner. Steht
                          ueber der Datenablage, weil der Ordner dort entsteht. */}
                      <GwGSuggestionAlert
                        isVisible={
                          gwgLegalPersonNameArray.length > 0 &&
                          gwgSuggestionDismissedAt === null &&
                          !documentDirectoryArray.some(
                            directory => directory.DirectoryType === 'gwg'
                          )
                        }
                        legalPersonNameArray={gwgLegalPersonNameArray}
                        onCreate={() =>
                          setGwgCreateSignal(previous => previous + 1)
                        }
                        onDismiss={() =>
                          setGwgSuggestionDismissedAt(
                            new Date()
                              .toISOString()
                              .slice(0, 19)
                              .replace('T', ' ')
                          )
                        }
                      />
                      <DocumentDirectoryOverview
                        idLegalTransaction={idLegalTransaction}
                        documentArray={documentArray}
                        documentDirectoryArray={documentDirectoryArray}
                        setDocumentArray={setDocumentArray}
                        setDocumentDirectoryArray={setDocumentDirectoryArray}
                        downloadedArray={downloadedDocuments}
                        assignedLegalClients={assignedLegalClients}
                        gwgLegalPersonArray={gwgLegalPersonArray}
                        systemSource={{
                          legalPhaseArray: legalPhaseFullObjectArray,
                          certificateArray: certificationArray,
                          invoiceArray: invoiceArray,
                          invoiceObjectArray: invoiceObjectArray,
                          gwgArray: gwgArray,
                        }}
                        gwgFolderArray={
                          legalTransactionFullObject?.GwGFolderArray
                        }
                        gwgFolderChangeArray={gwgFolderChangeArray}
                        onGwGFolderChange={handleGwGFolderChange}
                        meetingArray={meetingArray}
                        isCalendarEnabled={calendarModuleActive}
                        legalClientDocumentTypeArray={legalClientDocumentTypes}
                        gwgCreateSignal={gwgCreateSignal}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={13}>
                      <SpecialFunctionOverview
                        idLegalTransaction={idLegalTransaction}
                        legalTransaction_has_LegalTransactionSpecialFunctionArray={
                          legalTransaction_has_LegalTransactionSpecialFunctionArray
                        }
                        setLegalTransaction_has_LegalTransactionSpecialFunctionArray={
                          setLegalTransaction_has_LegalTransactionSpecialFunctionArray
                        }
                        gwgArray={gwgArray}
                        setGwgArray={setGwgArray}
                        legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                        legalReqState={legalRequirementStates}
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={14}>
                      {legalTransactionObject?.idLegalTransaction != null && (
                        <RatingFormTab
                          idLegalTransaction={
                            legalTransactionObject.idLegalTransaction
                          }
                          idLegalTransactionState={
                            legalTransactionObject.idLegalTransactionState
                          }
                          closedAt={legalTransactionObject.Closed_at ?? null}
                          legalPhases={legalPhaseFullObjectArray}
                        />
                      )}
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={15}>
                      <Typography variant='h6' sx={{ mb: 0.5 }}>
                        DiNo Ident-Service
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 2 }}
                      >
                        Identifizierung der Beteiligten in zwei Schritten: vorab
                        Selbsteinreichung per Mandantenportal, bei der
                        Beurkundung erneute Validierung per Tablet.
                      </Typography>
                      {tabletServiceActive ? (
                        <TabletActionPanel
                          idLegalTransaction={idLegalTransaction}
                          participants={assignedLegalClients}
                          portalSelection={portalCheckSelection}
                          setPortalSelection={setPortalCheckSelection}
                          drafts={draftArray}
                          certificates={certificationArray}
                          otherDocuments={otherDocumentArray}
                          internalDocuments={documentArray}
                        />
                      ) : (
                        <Typography variant='body2' color='text.secondary'>
                          Die Erweiterung „DiNo Ident-Service" ist nicht aktiv.
                        </Typography>
                      )}
                    </CustomTabPanel>

                    <CustomTabPanel value={currentTab} index={16}>
                      <Typography variant='h6'>Log</Typography>

                      {/* Neues zentrales Änderungsprotokoll (#1330). Lädt erst,
                          wenn der Reiter offen ist — der Log wird im normalen
                          Gebrauch nicht gebraucht und soll das Öffnen eines
                          Vorgangs nicht verlangsamen. */}
                      <Box sx={{ mt: 2 }}>
                        <ActivityLog
                          entityType='LegalTransaction'
                          entityId={idLegalTransaction}
                          enabled={currentTab === 16}
                          areas={ACTIVITY_LOG_AREAS}
                        />
                      </Box>

                      {/* Bisheriges Vorgangs-Log -- eingeklappt, damit das
                          zentrale Protokoll nicht unter einer Wand alter
                          Zeilen verschwindet. Wird mit der Legacy-Migration
                          abgelöst. */}
                    </CustomTabPanel>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            {isButtonDisabled() && (
              <Alert severity='warning' sx={{ marginBottom: 2 }}>
                Es existieren ungespeicherte Daten.
              </Alert>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {isClosed && (
              <Button
                variant='outlined'
                onClick={handleReactive}
                disabled={isPending}
              >
                Reaktivieren
              </Button>
            )}

            {/*
              #1283: manueller Health Check. Hier gibt es kein „Dennoch
              speichern" — der Check ist nicht Teil eines Speichervorgangs.
              Nur mit Freischaltung (/admin/dev/healthcheck).
            */}
            {isHealthCheckEnabled && (
              <Button
                variant='outlined'
                onClick={() => {
                  runHealthCheckNow({
                    legalTransaction: createLegalTransactionSaveObject(),
                    context: healthCheckContext(),
                  });
                  setPendingHealthCheckSave(null);
                  setIsHealthCheckDialogOpen(true);
                }}
              >
                Health Check
              </Button>
            )}

            <Button
              variant={isClosed ? 'contained' : 'outlined'}
              onClick={() => {
                if (isPendingUpdateLegalTransactionFullObject) return;
                handleClose(checkIfEdited());
              }}
            >
              Schließen
            </Button>
            {!isClosed && (
              <>
                <Button
                  variant='contained'
                  onClick={() =>
                    legalTransactionObject &&
                    legalTransactionFullObject &&
                    legalTransactionObject.idLegalTransactionType !==
                      legalTransactionFullObject.idLegalTransactionType
                      ? setIsOpenChangeType(true)
                      : handleSave()
                  }
                  disabled={isPending || isButtonDisabled()}
                >
                  {isPending ? (
                    <Box display='flex' alignItems='center' gap={1}>
                      <span>Speichern</span>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    'Speichern '
                  )}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/*/////////////////////////////////////////// NEUER UNTERLAGEN SENDEN ///////////////////////////////////////////  */}

        <Dialog
          open={showVorgangWillBeSavedDialog}
          onClose={() => setShowVorgangWillBeSavedDialog(false)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Unterlagen senden</DialogTitle>

          <DialogContent>
            Wenn Sie fortfahren möchten, muss der Vorgang zunächst gespeichert
            werden. Bitte klicken Sie hierfür auf „Weiter“.
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setShowVorgangWillBeSavedDialog(false)}
              color='primary'
              variant='outlined'
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                handleSave(undefined, true);
                setShowVorgangWillBeSavedDialog(false);
              }}
              color='primary'
              variant='contained'
            >
              Weiter
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showSendDocumentsDialogNew}
          onClose={() => setShowSendDocumentsDialogNew(false)}
          maxWidth='lg'
          fullWidth
        >
          <DialogTitle>Unterlagen senden </DialogTitle>
          <DialogContent>
            <SendDocumentNew
              legalTransactionFullObject={legalTransactionFullObject}
              sendDocument={sendDocumentNew}
              setSendDocument={setSendDocumentNew}
            ></SendDocumentNew>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowSendDocumentsDialogNew(false)}
              color='primary'
              variant='outlined'
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => setShowSendDocumentsDialogConfirmation(true)}
              disabled={
                sendDocumentNew.legalClientArray.length < 1 ||
                ((sendDocumentNew.documentTypeArray?.length ?? 0) < 1 &&
                  (sendDocumentNew.selectedDocumentIds?.length ?? 0) < 1 &&
                  (sendDocumentNew.selectedDocuments?.length ?? 0) < 1)
              }
              color='primary'
              variant='contained'
            >
              Senden
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showSendDocumentsDialogConfirmation}
          onClose={() => setShowSendDocumentsDialogConfirmation(false)}
        >
          <DialogTitle>
            Wie sollen die Unterlagen bereitgestellt werden?
          </DialogTitle>
          <DialogContent>
            <FormControl
              fullWidth
              variant='outlined'
              error={false}
              margin='dense'
            >
              <InputLabel id='send-documents-label'>
                Bereitstellungsart
              </InputLabel>
              <Select
                labelId='send-documents-label'
                value={sendDocumentNew.sendOption}
                onChange={event =>
                  setSendDocumentNew({
                    ...sendDocumentNew,
                    sendOption: event.target
                      .value as ISendDocumentNewSendOption,
                  })
                }
                label='Bereitstellungsart'
              >
                <MenuItem value={SendOption.Documentportal}>
                  DiNo-Dokumentenportal
                </MenuItem>
                <MenuItem value={SendOption.Email}>
                  E-Mail
                  <Typography
                    component='span'
                    sx={{ fontStyle: 'italic', fontSize: '0.8em', ml: 1 }}
                  >
                    (Nicht GNotDS-konform!)
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowSendDocumentsDialogConfirmation(false);
              }}
              color='primary'
              variant='outlined'
            >
              Abbrechen
            </Button>
            <Button
              color='primary'
              variant='contained'
              onClick={() => {
                handleSendDocuments();
                setShowSendDocumentsDialogConfirmation(false);
                setShowSendDocumentsDialogNew(false);
              }}
            >
              Senden
            </Button>
          </DialogActions>
        </Dialog>

        {/*
          #1283: Ergebnis des Health Checks. „Dennoch speichern" erscheint nur,
          wenn der Check aus dem Speichervorgang kommt (`pendingHealthCheckSave`
          ist dann gesetzt) — beim manuellen Auslösen bewusst nicht.
        */}
        <HealthCheckDialog
          open={isHealthCheckDialogOpen}
          result={healthCheckResult}
          ruleStates={healthCheckRuleStates}
          isPartOfSave={pendingHealthCheckSave !== null}
          onClose={() => {
            setIsHealthCheckDialogOpen(false);
            setPendingHealthCheckSave(null);
          }}
          onSaveAnyway={() => {
            const pending = pendingHealthCheckSave;
            setIsHealthCheckDialogOpen(false);
            setPendingHealthCheckSave(null);
            void persistLegalTransaction(
              pending?.migrateType,
              pending?.openUnterlagenSenden,
              healthCheckResult
            );
          }}
          onToggleRule={toggleHealthCheckRule}
          onResetRule={resetHealthCheckRule}
          onJumpToTab={setCurrentTab}
        />

        <Dialog open={isOpenDelete} onClose={() => setIsOpenDelete(false)}>
          <DialogTitle>Löschen</DialogTitle>
          <DialogContent>
            Sind Sie sich sicher, dass Sie den aktuellen Vorgang löschen wollen?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsOpenDelete(false)}>Abbruch</Button>
            <Button
              variant='contained'
              onClick={handleDelete}
              disabled={isPendingDeleteLegalTransaction}
            >
              {isPendingDeleteLegalTransaction ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Löschen</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Löschen'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isOpenChangeType}
          onClose={() => setIsOpenChangeType(false)}
        >
          <DialogTitle>Veränderung der Rechtsgeschäftsart</DialogTitle>
          <DialogContent>
            Wollen Sie den aktuellen Vollzugsstand (Abwicklung) und das
            bestehende Rollenschema der Mandanten beibehalten oder auf den
            Vollzugsstand und das Rollenschema der neuen Rechtsgeschäftsart
            migrieren?
            <br />
            Bei einer Migration geht Ihr aktueller Stand der Abwicklung sowie
            die Rollenverteilung der Mandanten verloren.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsOpenChangeType(false)}>Abbruch</Button>
            <Button onClick={() => handleSave()}>Beibehalten</Button>
            <Button onClick={() => handleSave(true)}>Migrieren</Button>
          </DialogActions>
        </Dialog>

        {showAddCustomIdDialog && (
          <AddCustomIdDialog
            clients={assignedLegalClients.filter(
              c =>
                !customIdsWithContact.some(
                  x => x.idLegalClient === c.idLegalClient
                )
            )}
            onAddCustomId={(contactId: number, customId: string) => {
              handleChangeCustomIds(contactId, customId);
              setShowAddCustomIdDialog(false);
            }}
            onClose={() => setShowAddCustomIdDialog(false)}
          />
        )}
      </DocumentTransferProvider>
    </SidepanelDocumentContext.Provider>
  );
};
